import { useEffect } from "react";
import { ServiceTypeEnum, serviceApis } from "@payping/api";
import type { ServiceDetailViewModel } from "@payping/api";
import { Delete, RedoOutlined, Wrench } from "@payping/icons";
import { createService, deleteService, updateService } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Input,
  LegacyForm,
  Space,
  message,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch } from "@/utils/redux";

import { servicesAsPluginList } from "../data";

interface Props {
  plugin: (typeof servicesAsPluginList)[keyof typeof servicesAsPluginList];
}

interface MessageFormType {
  apiKey?: string;
  getPayNotify?: boolean;
  getImportantNotifies?: boolean;
  hookUrl?: string;
  merchantId?: number;
  phoneNumber?: string;
  sendDescription?: string;
  sendReceipt?: boolean;
}

const Messaging = ({ plugin }: Props) => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm();
  const dispatch = useAppDispatch();

  const {
    loading: loadingServiceDetails,
    value: serviceDetails,
    retry: retryServiceDetails,
  } = useMessagedAsyncRetry(async () => serviceApis.serviceOneService(plugin.serviceType), [plugin.serviceType], {
    useProgress: true,
  });

  useEffect(() => {
    if (serviceDetails) {
      const { apiKey, getImportantNotifies, getPayNotify, phoneNumber } = serviceDetails;
      form.setFieldsValue({ apiKey, getImportantNotifies, getPayNotify, phoneNumber });
    }
  }, [form, serviceDetails]);

  const [{ loading: loadingCreate }, handleCreate] = useMessagedAsyncFn(
    async (values: MessageFormType) => {
      const response = (await dispatch(
        createService({ ...values, serviceType: plugin.serviceType }),
      )) as unknown as Response;
      const url = await response.text();
      retryServiceDetails();
      message.success({ content: t("services.detail.kavenegar.message.successCreate") });
      return url.replace(/"/g, "");
    },
    [dispatch, plugin.serviceType, t, retryServiceDetails],
    undefined,
    { useProgress: true },
  );

  const [{ loading: loadingEdit }, handleEdit] = useMessagedAsyncFn(
    async (data: ServiceDetailViewModel) => {
      const response = await dispatch(updateService(data));
      retryServiceDetails();
      message.success({ content: t("services.detail.kavenegar.message.successEdit") });
      return response;
    },
    [dispatch, t, retryServiceDetails],
    undefined,
    { useProgress: true },
  );

  const [{ loading: loadingDelete }, handleDelete] = useMessagedAsyncFn(
    async (data: ServiceDetailViewModel) => {
      const response = await dispatch(deleteService(data));
      retryServiceDetails();
      form.resetFields();
      message.success({ content: t("services.detail.kavenegar.message.successDelete") });
      return response;
    },
    [dispatch, form, t, retryServiceDetails],
    undefined,
    { useProgress: true },
  );

  const inputItems = (serviceType: number) => {
    switch (serviceType) {
      case ServiceTypeEnum.Slack: // Slack
        return (
          <LegacyForm.Item label={`${t("common.address")} webhook`} name={"hookUrl"}>
            <Input />
          </LegacyForm.Item>
        );
      case ServiceTypeEnum.KavehNegar: // KavehNegar
        return (
          <>
            <LegacyForm.Item
              label={t("services.detail.messaging.codePrefix", { name: "apiKey" })}
              required
              rules={[{ required: true }]}
              name={"apiKey"}
            >
              <Input />
            </LegacyForm.Item>
            <LegacyForm.Item
              label={t("services.detail.messaging.kavehNegarNumber")}
              required
              rules={[{ required: true }]}
              name={"phoneNumber"}
            >
              <Input />
            </LegacyForm.Item>
          </>
        );
      case ServiceTypeEnum.MailerLite: // MailerLite
        return (
          <LegacyForm.Item
            label={t("services.detail.messaging.codePrefix", { name: "apiKey" })}
            required
            rules={[{ required: true }]}
            name={"apiKey"}
          >
            <Input />
          </LegacyForm.Item>
        );
      case ServiceTypeEnum.Telegram: // Telegram
        return null;
      default:
        return null;
    }
  };
  const loading = loadingServiceDetails || loadingCreate || loadingEdit || loadingDelete;

  return (
    <LegacyForm
      form={form}
      onFinish={async (values: MessageFormType) => {
        const data = {
          ...values,
          phoneNumber: String(values.phoneNumber),
          serviceType: plugin.serviceType,
        };
        if (serviceDetails) {
          await handleEdit(data);
        } else if (data.serviceType === ServiceTypeEnum.Telegram) {
          const result = await handleCreate(data);
          const newWindow = window.open(result, "_blank");
          if (result) newWindow?.location?.assign?.(result);
        } else {
          await handleCreate(data);
        }
      }}
      layout={"vertical"}
    >
      {inputItems(plugin.serviceType)}
      <LegacyForm.Item name={"getPayNotify"} valuePropName={"checked"}>
        <Checkbox disabled={loading}>{t("services.detail.messaging.reportMessage")}</Checkbox>
      </LegacyForm.Item>
      <LegacyForm.Item name={"getImportantNotifies"} valuePropName={"checked"}>
        <Checkbox disabled={loading}>{t("services.detail.messaging.specialMessage")}</Checkbox>
      </LegacyForm.Item>
      <Space>
        <Button
          loading={loading}
          htmlType="submit"
          icon={serviceDetails ? <RedoOutlined width={14} height={14} /> : <Wrench width={14} height={14} />}
          type={"primary"}
        >
          {t(serviceDetails ? "update" : "install")}
        </Button>
        {serviceDetails ? (
          <Button
            loading={loading}
            icon={<Delete width={14} height={14} />}
            type={"default"}
            danger
            onClick={() => handleDelete({ serviceType: plugin.serviceType })}
          >
            {t("delete")}
          </Button>
        ) : null}
      </Space>
    </LegacyForm>
  );
};

export default Messaging;
